import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconX,
  IconCalendar,
  IconUsers,
  IconBuilding,
  IconInfoCircle,
  IconTrash,
  IconUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconChevronDown,
  IconChevronUp,
  IconAlertCircle,
  IconCircleCheck,
  IconClock,
  IconEye,
  IconDotsVertical,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import {
  fetchHrEmployees,
  fetchHrDepartments,
  createHrHoliday,
  updateHrHoliday,
  fetchHrHolidayCalendar,
  fetchHrUpcomingHolidays,
  deleteHrHoliday,
  HrHolidayPayload,
} from "../../../apis/hrApi";

const DEFAULT_NATIONAL_HOLIDAYS = [
  {
    name: "Independence Day",
    date: "15 Aug 2026",
    color: "#EF4444",
    raw: {
      title: "Independence Day",
      description: "National Holiday",
      holidayDate: "2026-08-15T00:00:00.000Z",
      holidayType: "NATIONAL",
      mode: "SINGLE",
      applyTo: "COMPANY"
    }
  },
  {
    name: "Raksha Bandhan",
    date: "28 Aug 2026",
    color: "#F59E0B",
    raw: {
      title: "Raksha Bandhan",
      description: "National Holiday",
      holidayDate: "2026-08-28T00:00:00.000Z",
      holidayType: "NATIONAL",
      mode: "SINGLE",
      applyTo: "COMPANY"
    }
  },
  {
    name: "Janmashtami",
    date: "04 Sep 2026",
    color: "#3B82F6",
    raw: {
      title: "Janmashtami",
      description: "National Holiday",
      holidayDate: "2026-09-04T00:00:00.000Z",
      holidayType: "NATIONAL",
      mode: "SINGLE",
      applyTo: "COMPANY"
    }
  },
  {
    name: "Dussehra",
    date: "20 Oct 2026",
    color: "#10B981",
    raw: {
      title: "Dussehra",
      description: "National Holiday",
      holidayDate: "2026-10-20T00:00:00.000Z",
      holidayType: "NATIONAL",
      mode: "SINGLE",
      applyTo: "COMPANY"
    }
  },
  {
    name: "Diwali Vacation",
    date: "07 Nov 2026",
    color: "#8B5CF6",
    raw: {
      title: "Diwali Vacation",
      description: "National Holiday",
      holidayDate: "2026-11-07T00:00:00.000Z",
      holidayType: "NATIONAL",
      mode: "SINGLE",
      applyTo: "COMPANY"
    }
  }
];

const IMPORT_FIELDS = [
  { field: "title", required: true, description: "Title of the holiday" },
  { field: "mode", required: true, description: "SINGLE, BULK, or SELECTED_DAYS" },
  { field: "applyTo", required: true, description: "COMPANY, DEPARTMENT, or EMPLOYEE" },
  { field: "holidayDate", required: false, description: "YYYY-MM-DD (required for SINGLE)" },
  { field: "startDate", required: false, description: "YYYY-MM-DD (required for BULK/SELECTED_DAYS)" },
  { field: "endDate", required: false, description: "YYYY-MM-DD (required for BULK/SELECTED_DAYS)" },
  { field: "selectedDays", required: false, description: "Comma-separated days, e.g. MONDAY,FRIDAY" },
  { field: "description", required: false, description: "Optional description" },
];

// Mock database lists (fallback if API is loading or fails)
const EMPLOYEES_LIST = [
  "Sushil Rathore",
  "Priya Sharma",
  "Rahul Verma",
  "Anita Desai",
  "Karan Mehta",
  "Neha Gupta",
  "Vikram Singh",
  "Divya Nair",
  "Arjun Kapoor",
  "Sneha Joshi",
];

const DEPARTMENTS_LIST = [
  "Human Resources",
  "IT & Support",
  "QA & Testing",
  "Marketing",
  "Finance",
  "Sales",
];

const NICE_COLORS = [
  "#EF4444", // Red
  "#F59E0B", // Amber
  "#10B981", // Emerald
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#14B8A6", // Teal
  "#6366F1", // Indigo
];

// Helper to format Date to YYYY-MM-DD
const toInputDateStr = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Helper to format Date to "DD MMM YYYY"
const formatDateStr = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper to convert YYYY-MM-DD to "DD MMM YYYY"
const fromInputDateStr = (str: string) => {
  if (!str) return "";
  const parts = str.split("-");
  if (parts.length !== 3) return "";
  const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  return formatDateStr(d);
};

// Helper to parse "DD MMM YYYY" into Date
const parseHolidayDate = (dateStr: string) => {
  const parts = dateStr.split(" ");
  if (parts.length !== 3) return new Date();
  const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const m = months.indexOf(parts[1].toLowerCase());
  return new Date(parseInt(parts[2]), m === -1 ? 0 : m, parseInt(parts[0]));
};

// ── ADD HOLIDAY FORM ─────────────────────────────────────────────────────────
interface AddHolidayFormProps {
  onClose: () => void;
  initialDate: Date | null;
  initialMode: "single" | "bulk" | "selected";
  initialHoliday: any | null;
  employeesList: any[];
  departmentsList: any[];
  onAddHoliday: (data: {
    id?: string;
    mode: "single" | "bulk" | "selected";
    actionType: "create" | "delete";
    applyTo: { employee: boolean; department: boolean };
    employees: string[];
    departments: string[];
    title: string;
    date: string;
    startDate: string;
    endDate: string;
    description: string;
    isWeekOff: boolean;
    selectedDays?: string[];
  }) => void;
}

function AddHolidayForm({
  onClose,
  initialDate,
  initialMode,
  initialHoliday,
  employeesList,
  departmentsList,
  onAddHoliday,
}: AddHolidayFormProps) {
  const [mode, setMode] = useState<"single" | "bulk" | "selected">("single");
  const [actionType, setActionType] = useState<"create" | "delete">("create");
  const [applyToEmployee, setApplyToEmployee] = useState(false);
  const [applyToDepartment, setApplyToDepartment] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [isWeekOff, setIsWeekOff] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    
    if (initialHoliday) {
      setMode(initialHoliday.mode?.toLowerCase() || "single");
      setActionType("create");
      setApplyToEmployee(initialHoliday.applyTo === "EMPLOYEE");
      setApplyToDepartment(initialHoliday.applyTo === "DEPARTMENT");
      setSelectedEmployees(initialHoliday.employeeIds || []);
      setSelectedDepartments(initialHoliday.departmentIds || []);
      setTitle(initialHoliday.title || "");
      setIsWeekOff(initialHoliday.holidayType === "COMPANY");
      setDescription(initialHoliday.description || "");
      setSelectedDays(initialHoliday.selectedDays || []);
      
      if (initialHoliday.holidayDate) {
        const dateStr = initialHoliday.holidayDate.split("T")[0];
        setDate(dateStr);
        setStartDate(dateStr);
        setEndDate(dateStr);
      } else {
        setDate("");
        setStartDate(initialHoliday.startDate?.split("T")[0] || "");
        setEndDate(initialHoliday.endDate?.split("T")[0] || "");
      }
    } else {
      setMode(initialMode);
      setActionType("create");
      setApplyToEmployee(false);
      setApplyToDepartment(false);
      setSelectedEmployees([]);
      setSelectedDepartments([]);
      setTitle("");
      setIsWeekOff(false);
      setDescription("");
      setSelectedDays([]);

      if (initialDate) {
        const dateStr = toInputDateStr(initialDate);
        setDate(dateStr);
        setStartDate(dateStr);
        setEndDate(dateStr);
      } else {
        const todayStr = toInputDateStr(new Date());
        setDate(todayStr);
        setStartDate(todayStr);
        setEndDate(todayStr);
      }
    }
  }, [initialDate, initialMode, initialHoliday]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (actionType === "create" && !title.trim()) {
      setError("Please enter a holiday title");
      return;
    }
    if (mode === "single" && !date) {
      setError("Please select a date");
      return;
    }
    if (mode === "bulk" && (!startDate || !endDate)) {
      setError("Please select both start and end dates");
      return;
    }
    if (mode === "bulk" && startDate > endDate) {
      setError("Start date cannot be after end date");
      return;
    }
    if (mode === "selected" && (!startDate || !endDate)) {
      setError("Please select both start and end dates");
      return;
    }
    if (mode === "selected" && startDate > endDate) {
      setError("Start date cannot be after end date");
      return;
    }
    if (mode === "selected" && selectedDays.length === 0) {
      setError("Please select at least one day of the week");
      return;
    }

    onAddHoliday({
      id: initialHoliday?.id,
      mode,
      actionType,
      applyTo: { employee: applyToEmployee, department: applyToDepartment },
      employees: selectedEmployees,
      departments: selectedDepartments,
      title,
      date,
      startDate,
      endDate,
      description,
      isWeekOff,
      selectedDays,
    });
  };

  // Section header component
  const SectionHeader = ({
    icon: Icon,
    title,
    className = "border-b border-[#FBD037]/30 pb-2 mb-3",
  }: {
    icon: any;
    title: string;
    className?: string;
  }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#FCFDF8]">
      {/* Form Body */}
      <form onSubmit={handleSubmit} className="p-4 flex-1 overflow-y-auto space-y-4 scrollbar-clean">
        {error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs font-semibold text-red-600 flex items-center gap-2">
            <IconInfoCircle size={16} />
            {error}
          </div>
        )}

        {/* SECTION 1: HOLIDAY MODE & CONFIGURATION */}
        <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl p-4 transition-all duration-300 space-y-4 relative border border-white/20 shadow-sm">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
          <div className="relative space-y-4">
            <SectionHeader icon={IconCalendar} title="Holiday Configuration" />

            {/* Holiday Mode */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Holiday Mode</label>
              <div className="grid grid-cols-3 gap-2">
                <label className={`flex flex-col p-3 rounded-2xl border cursor-pointer transition ${mode === "single" ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200 hover:border-gray-300"}`}>
                  <input
                    type="radio"
                    name="holidayMode"
                    checked={mode === "single"}
                    onChange={() => setMode("single")}
                    className="sr-only"
                  />
                  <span className="text-xs font-bold text-[#212121]">Single</span>
                  <span className="text-[9px] text-gray-400 mt-0.5">One holiday</span>
                </label>

                <label className={`flex flex-col p-3 rounded-2xl border cursor-pointer transition ${mode === "bulk" ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200 hover:border-gray-300"}`}>
                  <input
                    type="radio"
                    name="holidayMode"
                    checked={mode === "bulk"}
                    onChange={() => setMode("bulk")}
                    className="sr-only"
                  />
                  <span className="text-xs font-bold text-[#212121]">Bulk</span>
                  <span className="text-[9px] text-gray-400 mt-0.5">Date range</span>
                </label>

                <label className={`flex flex-col p-3 rounded-2xl border cursor-pointer transition ${mode === "selected" ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200 hover:border-gray-300"}`}>
                  <input
                    type="radio"
                    name="holidayMode"
                    checked={mode === "selected"}
                    onChange={() => setMode("selected")}
                    className="sr-only"
                  />
                  <span className="text-xs font-bold text-[#212121]">Selected</span>
                  <span className="text-[9px] text-gray-400 mt-0.5">Weekdays</span>
                </label>
              </div>
            </div>

            {/* Action Type & Apply To Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Action Type */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Action Type</label>
                <div className="flex flex-col gap-2">
                  <label className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition ${actionType === "create" ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200"}`}>
                    <input
                      type="radio"
                      name="actionType"
                      checked={actionType === "create"}
                      onChange={() => setActionType("create")}
                      className="accent-[#FBD037]"
                    />
                    <span className="text-xs font-semibold text-[#212121]">Create</span>
                  </label>
                  <label className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition ${actionType === "delete" ? "border-red-300 bg-red-50/50" : "border-gray-200"}`}>
                    <input
                      type="radio"
                      name="actionType"
                      checked={actionType === "delete"}
                      onChange={() => setActionType("delete")}
                      className="accent-red-500"
                    />
                    <span className="text-xs font-semibold text-red-600 flex items-center gap-1"><IconTrash size={14}/> Delete</span>
                  </label>
                </div>
              </div>

              {/* Apply To */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Apply To</label>
                <div className="flex flex-col gap-2">
                  <label className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition ${applyToEmployee ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200"}`}>
                    <input
                      type="checkbox"
                      checked={applyToEmployee}
                      onChange={(e) => setApplyToEmployee(e.target.checked)}
                      className="accent-[#FBD037] rounded"
                    />
                    <span className="text-xs font-semibold text-[#212121] flex items-center gap-1.5"><IconUsers size={14}/> Employee</span>
                  </label>
                  <label className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition ${applyToDepartment ? "border-[#FBD037] bg-[#FBD037]/5" : "border-gray-200"}`}>
                    <input
                      type="checkbox"
                      checked={applyToDepartment}
                      onChange={(e) => setApplyToDepartment(e.target.checked)}
                      className="accent-[#FBD037] rounded"
                    />
                    <span className="text-xs font-semibold text-[#212121] flex items-center gap-1.5"><IconBuilding size={14}/> Dept.</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Conditional Dropdown Selection: Employees */}
            {applyToEmployee && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Select Employees <span className="text-red-500">*</span></label>
                <div className="flex flex-wrap gap-1.5 p-1.5 border border-gray-200 rounded-xl bg-white min-h-[42px] items-center focus-within:border-[#FBD037] focus-within:ring-1 focus-within:ring-[#FBD037] transition">
                  {selectedEmployees.map(empId => {
                    const empObj = employeesList.find(x => (x.id || x._id) === empId);
                    const dispName = empObj ? `${empObj.firstName} ${empObj.lastName}` : empId;
                    return (
                      <span key={empId} className="flex items-center gap-1 px-2.5 py-1 bg-[#FBD037]/20 border border-[#FBD037]/30 text-xs font-bold text-[#212121] rounded-full animate-in scale-in duration-150">
                        {dispName}
                        <button type="button" onClick={() => setSelectedEmployees(prev => prev.filter(x => x !== empId))} className="hover:text-red-500 cursor-pointer"><IconX size={12} /></button>
                      </span>
                    );
                  })}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !selectedEmployees.includes(e.target.value)) {
                        setSelectedEmployees(prev => [...prev, e.target.value]);
                      }
                    }}
                    className="flex-1 min-w-[120px] p-1 text-sm bg-transparent outline-none border-none cursor-pointer text-gray-500"
                  >
                    <option value="" disabled>Add employee...</option>
                    {employeesList.map(emp => {
                      const empId = emp.id || emp._id;
                      const empName = `${emp.firstName} ${emp.lastName}`;
                      return (
                        <option key={empId} value={empId}>{empName}</option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}

            {/* Conditional Dropdown Selection: Departments */}
            {applyToDepartment && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Select Department(s) <span className="text-red-500">*</span></label>
                <div className="flex flex-wrap gap-1.5 p-1.5 border border-gray-200 rounded-xl bg-white min-h-[42px] items-center focus-within:border-[#FBD037] focus-within:ring-1 focus-within:ring-[#FBD037] transition">
                  {selectedDepartments.map(deptId => {
                    const deptObj = departmentsList.find(x => (x.id || x._id) === deptId);
                    const dispName = deptObj ? deptObj.departmentName : deptId;
                    return (
                      <span key={deptId} className="flex items-center gap-1 px-2.5 py-1 bg-[#FBD037]/20 border border-[#FBD037]/30 text-xs font-bold text-[#212121] rounded-full animate-in scale-in duration-150">
                        {dispName}
                        <button type="button" onClick={() => setSelectedDepartments(prev => prev.filter(x => x !== deptId))} className="hover:text-red-500 cursor-pointer"><IconX size={12} /></button>
                      </span>
                    );
                  })}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !selectedDepartments.includes(e.target.value)) {
                        setSelectedDepartments(prev => [...prev, e.target.value]);
                      }
                    }}
                    className="flex-1 min-w-[120px] p-1 text-sm bg-transparent outline-none border-none cursor-pointer text-gray-500"
                  >
                    <option value="" disabled>Add department...</option>
                    {departmentsList.map(dept => {
                      const deptId = dept.id || dept._id;
                      return (
                        <option key={deptId} value={deptId}>{dept.departmentName}</option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 2: HOLIDAY DETAILS */}
        <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl p-4 transition-all duration-300 space-y-4 relative border border-white/20 shadow-sm">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
          <div className="relative space-y-4">
            <SectionHeader icon={IconInfoCircle} title="Holiday Details" />

            {/* Title - Required only for Create */}
            {actionType === "create" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Enter holiday title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                />
              </div>
            )}

            {/* Date Picker fields */}
            {mode === "single" ? (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Start Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">End Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                    />
                  </div>
                </div>

                {mode === "selected" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Selected Days <span className="text-red-500">*</span></label>
                    <div className="flex gap-1 justify-between bg-white p-1.5 border border-gray-250 rounded-xl">
                      {["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].map((day) => {
                        const isSelected = selectedDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedDays(prev => prev.filter(d => d !== day));
                              } else {
                                setSelectedDays(prev => [...prev, day]);
                              }
                            }}
                            className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg border transition ${
                              isSelected
                                ? "bg-[#FBD037] border-[#FBD037] text-[#212121]"
                                : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                            }`}
                          >
                            {day.substring(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {actionType === "create" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Description</label>
                <textarea
                  placeholder="Enter holiday description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition resize-none"
                />
              </div>
            )}

            {/* Week Off Toggle */}
            <div className="flex items-center justify-between p-3 border border-gray-250 rounded-2xl bg-white">
              <div>
                <div className="text-sm font-bold text-[#212121]">Week Off</div>
                <div className="text-[10px] text-gray-400 font-semibold">Mark these days as weekly rest days</div>
              </div>
              <button
                type="button"
                onClick={() => setIsWeekOff(!isWeekOff)}
                className={`relative inline-flex w-11 h-6 rounded-full transition-colors duration-200 ease-in-out outline-none ${isWeekOff ? "bg-[#FBD037]" : "bg-gray-200"}`}
              >
                <span
                  className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition duration-200 ease-in-out mt-1 ${isWeekOff ? "translate-x-6" : "translate-x-1"}`}
                />
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 bg-white/50 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-[#D2D2D2] hover:bg-gray-100 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-5 py-2 bg-[#FBD037] text-[#212121] text-xs font-bold rounded-xl hover:bg-[#f5c71a] transition-colors shadow-sm cursor-pointer"
        >
          {actionType === "delete" ? "Delete Holiday" : "Save Holiday"}
        </button>
      </div>
    </div>
  );
}

// ── IMPORT HOLIDAY FORM ──────────────────────────────────────────────────────
interface ImportHolidayFormProps {
  onClose: () => void;
  onImportSuccess: () => void;
}

function ImportHolidayForm({ onClose, onImportSuccess }: ImportHolidayFormProps) {
  const [showFields, setShowFields] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE_MB = 20;

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File exceeds ${MAX_FILE_SIZE_MB}MB limit.`;
    }
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "xlsm", "csv"].includes(ext || "")) {
      return "Only .xlsx, .xls, .xlsm, or .csv files are allowed.";
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelect(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = "";
  };

  const handleRemoveFile = () => setSelectedFile(null);

  const handleDownloadSample = () => {
    // Generate CSV template
    const headers = IMPORT_FIELDS.map((f) => f.field).join(",");
    const sampleRow = [
      '"Republic Day"',
      '"SINGLE"',
      '"COMPANY"',
      '"2026-01-26"',
      '""',
      '""',
      '""',
      '"National Holiday"',
    ].join(",");
    const csvContent = `${headers}\n${sampleRow}`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "holiday_import_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sample template downloaded successfully!");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      return;
    }
    setIsUploading(true);
    try {
      // Simulate file import API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.success("Holidays imported successfully!");
      onImportSuccess();
    } catch (err: any) {
      toast.error(err.message || "Failed to import holidays.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Scrollable Form Body */}
      <div className="p-6 space-y-4 overflow-y-auto flex-1 text-slate-700">
        
        {/* Required Fields Checklist */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-1.5">
          <p className="text-xs font-semibold text-blue-700">
            Before uploading the sheet, please ensure that the following fields are included in the file:
          </p>
          <p className="text-[11px] text-blue-600">
            Make sure headers match exactly (case-insensitive). Missing fields may cause import errors.
          </p>
          <button
            type="button"
            onClick={() => setShowFields((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors mt-0.5 cursor-pointer"
          >
            {showFields ? (
              <>
                <IconChevronUp size={13} /> Hide fields
              </>
            ) : (
              <>
                <IconChevronDown size={13} /> Show fields
              </>
            )}
          </button>

          {showFields && (
            <div className="mt-3 border border-blue-100 rounded-xl overflow-hidden max-h-[150px] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-blue-100/60 sticky top-0">
                    <th className="text-left px-3 py-2 font-bold text-blue-700 bg-blue-100">Field</th>
                    <th className="text-left px-3 py-2 font-bold text-blue-700 bg-blue-100">Required</th>
                    <th className="text-left px-3 py-2 font-bold text-blue-700 bg-blue-100">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-50 bg-white">
                  {IMPORT_FIELDS.map((f) => (
                    <tr key={f.field} className="bg-white hover:bg-blue-50/30 transition-colors">
                      <td className="px-3 py-2 font-mono font-semibold text-slate-700">{f.field}</td>
                      <td className="px-3 py-2">
                        {f.required ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-bold">
                            <IconInfoCircle size={11} /> Yes
                          </span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{f.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sample File Block */}
        <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 gap-3">
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-700">Need a sample file?</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Download template & structure your data.</p>
          </div>
          <button
            type="button"
            onClick={handleDownloadSample}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#FBD037] bg-[#FBD037]/10 text-[11px] font-bold text-[#212121] hover:bg-[#FBD037]/20 transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <IconDownload size={13} />
            Sample File
          </button>
        </div>

        {/* Drop Zone */}
        {!selectedFile ? (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-3 transition-all duration-200 cursor-pointer ${
              isDragging
                ? "border-[#FBD037] bg-[#FBD037]/5 scale-[1.01]"
                : "border-gray-200 hover:border-[#FBD037] hover:bg-gray-50/50"
            }`}
          >
            <IconFileSpreadsheet size={36} className={`transition-colors ${isDragging ? "text-[#C9A100]" : "text-gray-300"}`} />
            <p className="text-xs font-semibold text-gray-500">
              {isDragging ? "Drop your file here!" : "Click or drag an Excel file here"}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-bold text-slate-600 hover:border-[#FBD037] hover:text-[#212121] transition shadow-sm cursor-pointer"
            >
              <IconUpload size={13} />
              Choose file
            </button>
            <p className="text-[10px] text-gray-400">
              Allowed: .xlsx, .xls, .xlsm, .csv · Max {MAX_FILE_SIZE_MB}MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
        ) : (
          /* File Preview */
          <div className="border border-emerald-200 bg-emerald-50 rounded-xl px-4 py-3 flex items-center justify-between gap-3 animate-in fade-in duration-200">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <IconCircleCheck size={20} className="text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-emerald-800 truncate">{selectedFile.name}</p>
                <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer shrink-0"
              title="Remove File"
            >
              <IconTrash size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 bg-[#F7F6F2] shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer"
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleImport}
          className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition cursor-pointer bg-[#FBD037] hover:bg-[#FACC15] text-[#212121]"
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? "Importing..." : "Open Editor"}
        </button>
      </div>
    </div>
  );
}

// ── FILTER HOLIDAY FORM ──────────────────────────────────────────────────────
interface FilterHolidayFormProps {
  onClose: () => void;
  employeesList: any[];
  departmentsList: any[];
  filterDepartmentId: string;
  setFilterDepartmentId: (id: string) => void;
  filterEmployeeId: string;
  setFilterEmployeeId: (id: string) => void;
  onClearFilters: () => void;
}

function FilterHolidayForm({
  onClose,
  employeesList,
  departmentsList,
  filterDepartmentId,
  setFilterDepartmentId,
  filterEmployeeId,
  setFilterEmployeeId,
  onClearFilters,
}: FilterHolidayFormProps) {
  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Body */}
      <div className="p-6 flex-1 overflow-y-auto space-y-4 text-slate-700">
        {/* Department Filter */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Department</label>
          <select
            value={filterDepartmentId}
            onChange={(e) => setFilterDepartmentId(e.target.value)}
            className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition cursor-pointer"
          >
            <option value="">All Departments</option>
            {departmentsList.map((dept) => (
              <option key={dept.id || dept._id} value={dept.id || dept._id}>
                {dept.departmentName}
              </option>
            ))}
          </select>
        </div>

        {/* Employee Filter */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Employee</label>
          <select
            value={filterEmployeeId}
            onChange={(e) => setFilterEmployeeId(e.target.value)}
            className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition cursor-pointer"
          >
            <option value="">All Employees</option>
            {employeesList.map((emp) => (
              <option key={emp.id || emp._id} value={emp.id || emp._id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 bg-[#F7F6F2] shrink-0">
        <button
          type="button"
          onClick={onClearFilters}
          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer"
        >
          Clear Filters
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition cursor-pointer bg-[#FBD037] hover:bg-[#FACC15] text-[#212121]"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ── MAIN HOLIDAY PAGE ────────────────────────────────────────────────────────
const mapApiHolidaysToUi = (apiData: any): any[] => {
  const rawList = Array.isArray(apiData)
    ? apiData
    : (apiData?.holidays || apiData?.data || apiData?.calendar || []);

  if (!Array.isArray(rawList)) return [];

  const mapped: any[] = [];
  rawList.forEach((h: any) => {
    const color = h.color || NICE_COLORS[Math.abs((h.title || "Holiday").charCodeAt(0)) % NICE_COLORS.length];
    const name = h.title || "Holiday";

    if (h.mode === "SINGLE" && h.holidayDate) {
      const dateParts = h.holidayDate.split("T")[0].split("-");
      if (dateParts.length === 3) {
        const d = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        mapped.push({
          id: h.id || h._id,
          name,
          date: formatDateStr(d),
          color,
          raw: h
        });
      }
    } else if (h.mode === "BULK" && h.startDate && h.endDate) {
      const start = new Date(h.startDate.split("T")[0]);
      const end = new Date(h.endDate.split("T")[0]);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        mapped.push({
          id: h.id || h._id,
          name,
          date: formatDateStr(d),
          color,
          raw: h
        });
      }
    } else if (h.mode === "SELECTED_DAYS" && h.startDate && h.endDate) {
      const start = new Date(h.startDate.split("T")[0]);
      const end = new Date(h.endDate.split("T")[0]);
      const daysOfWeek = Array.isArray(h.selectedDays) ? h.selectedDays.map((day: string) => day.toUpperCase()) : [];
      const dayMapping: Record<string, number> = {
        SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6
      };
      const activeDays = daysOfWeek.map((day: string) => dayMapping[day]).filter((idx: any) => idx !== undefined);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (activeDays.includes(d.getDay())) {
          mapped.push({
            id: h.id || h._id,
            name,
            date: formatDateStr(d),
            color,
            raw: h
          });
        }
      }
    } else if (h.holidayDate) {
      const dateParts = h.holidayDate.split("T")[0].split("-");
      if (dateParts.length === 3) {
        const d = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        mapped.push({
          id: h.id || h._id,
          name,
          date: formatDateStr(d),
          color,
          raw: h
        });
      }
    }
  });

  return mapped;
};

const formatFullDateString = (dateInput: any) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const formatShortDateString = (dateInput: any) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

export default function Holiday() {
  const [holidayMonth, setHolidayMonth] = useState({ year: 2026, month: 4 }); // 0-indexed (4 = May)
  const [holidays, setHolidays] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"calendar" | "table">("calendar");
  const [filterDepartmentId, setFilterDepartmentId] = useState("");
  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [rawHolidays, setRawHolidays] = useState<any[]>([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState<any[]>([]);

  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [rightPanelType, setRightPanelType] = useState<"add" | "import" | "filter" | "view" | "edit">("add");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHoliday, setSelectedHoliday] = useState<any | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingHoliday, setViewingHoliday] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<"single" | "bulk" | "selected">("single");
  const [isDotsMenuOpen, setIsDotsMenuOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingHolidayForm, setEditingHolidayForm] = useState<any | null>(null);

  // Fetch upcoming holidays from backend
  const loadUpcomingHolidays = async () => {
    try {
      const res = await fetchHrUpcomingHolidays(1, 15);
      if (res.success) {
        const mapped = mapApiHolidaysToUi(res.data);
        setUpcomingHolidays(mapped);
      }
    } catch (err: any) {
      console.error("Failed to load upcoming holidays", err);
    }
  };

  // Load Employees and Departments on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const empRes = await fetchHrEmployees(1, 1000);
        if (empRes.success && empRes.data?.employees) {
          setEmployees(empRes.data.employees);
        }
        const deptRes = await fetchHrDepartments(1, 100);
        if (deptRes.success && deptRes.data?.departments) {
          setDepartments(deptRes.data.departments);
        }
      } catch (err) {
        console.error("Failed to fetch metadata (employees/departments)", err);
      }
    };
    loadMetadata();
  }, []);

  // Fetch holidays for the selected month/year
  const loadCalendarHolidays = async () => {
    setIsLoading(true);
    try {
      const apiMonth = holidayMonth.month + 1; // 1-indexed for backend
      const res = await fetchHrHolidayCalendar(apiMonth, holidayMonth.year, filterEmployeeId || undefined);
      if (res.success) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : (res.data?.holidays || res.data?.data || res.data?.calendar || []);
        setRawHolidays(rawList);
        const mapped = mapApiHolidaysToUi(res.data);
        setHolidays(mapped);
      }
      // Load upcoming holidays in parallel
      await loadUpcomingHolidays();
    } catch (err: any) {
      console.error("Failed to fetch holiday calendar", err);
      toast.error(err.message || "Failed to load holiday calendar");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarHolidays();
  }, [holidayMonth, filterEmployeeId]);

  const handleHolidayClick = (holidayRaw: any) => {
    if (holidayRaw) {
      const d = holidayRaw.holidayDate ? new Date(holidayRaw.holidayDate) : (holidayRaw.startDate ? new Date(holidayRaw.startDate) : null);
      setSelectedDate(d);
      setViewingHoliday(holidayRaw);
      setRightPanelType("view");
      setIsRightPanelOpen(true);
    }
  };

  // Handler to open single holiday on date click
  const handleDayClick = (dayNum: number) => {
    const d = new Date(holidayMonth.year, holidayMonth.month, dayNum);
    setSelectedDate(d);
    
    // Find holiday on this day to see if one exists
    const padDay = String(dayNum).padStart(2, "0");
    const monthStr = d.toLocaleString("default", { month: "short" });
    const dayMatchStr = `${padDay} ${monthStr} ${holidayMonth.year}`;
    const holidayObj = holidays.find(
      (h) => h.date.toLowerCase() === dayMatchStr.toLowerCase()
    );

    if (holidayObj && holidayObj.raw) {
      handleHolidayClick(holidayObj.raw);
    }
  };


  // Handler to open single holiday on top button click
  const handleAddSingleClick = () => {
    setSelectedDate(null);
    setSelectedHoliday(null);
    setModalMode("single");
    setRightPanelType("add");
    setIsRightPanelOpen(true);
  };

  const handleAddHolidaySubmit = async (data: {
    id?: string;
    mode: "single" | "bulk" | "selected";
    actionType: "create" | "delete";
    applyTo: { employee: boolean; department: boolean };
    employees: string[];
    departments: string[];
    title: string;
    date: string;
    startDate: string;
    endDate: string;
    description: string;
    isWeekOff: boolean;
    selectedDays?: string[];
  }) => {
    const loader = toast.loading(data.actionType === "delete" ? "Deleting holiday..." : "Saving holiday...");
    try {
      if (data.actionType === "create") {
        const apiMode = data.mode === "single" ? "SINGLE" : data.mode === "bulk" ? "BULK" : "SELECTED_DAYS";
        let apiApplyTo: "EMPLOYEE" | "DEPARTMENT" | "BRANCH" | "COMPANY" = "COMPANY";
        if (data.applyTo.employee && data.employees.length > 0) {
          apiApplyTo = "EMPLOYEE";
        } else if (data.applyTo.department && data.departments.length > 0) {
          apiApplyTo = "DEPARTMENT";
        }

        const payload: any = {
          actionType: "CREATE",
          mode: apiMode,
          applyTo: apiApplyTo,
          title: data.title,
          description: data.description,
          holidayType: data.isWeekOff ? "COMPANY" : "NATIONAL",
          selectedDays: data.selectedDays || [],
          branchIds: [],
          departmentIds: apiApplyTo === "DEPARTMENT" ? data.departments : [],
          employeeIds: apiApplyTo === "EMPLOYEE" ? data.employees : [],
        };

        if (apiMode === "SINGLE") {
          payload.holidayDate = data.date;
        } else {
          payload.startDate = data.startDate;
          payload.endDate = data.endDate;
        }

        const res = await createHrHoliday(payload);
        if (res.success) {
          toast.success("Holiday created successfully", { id: loader });
          setIsRightPanelOpen(false);
          loadCalendarHolidays();
        } else {
          toast.error(res.message || "Failed to create holiday", { id: loader });
        }
      } else if (data.actionType === "delete") {
        // Delete holiday. Use ID if available, otherwise fallback to the configuration body
        const res = await deleteHrHoliday(data.id || {
          actionType: "DELETE",
          mode: data.mode === "single" ? "SINGLE" : data.mode === "bulk" ? "BULK" : "SELECTED_DAYS",
          applyTo: (data.applyTo.employee ? "EMPLOYEE" : data.applyTo.department ? "DEPARTMENT" : "COMPANY"),
          title: data.title,
          description: data.description,
          holidayDate: data.mode === "single" ? data.date : null,
          startDate: data.mode !== "single" ? data.startDate : null,
          endDate: data.mode !== "single" ? data.endDate : null,
          selectedDays: data.selectedDays || [],
          branchIds: [],
          departmentIds: data.departments,
          employeeIds: data.employees,
        });

        if (res.success) {
          toast.success("Holiday deleted successfully", { id: loader });
          setIsRightPanelOpen(false);
          loadCalendarHolidays();
        } else {
          toast.error(res.message || "Failed to delete holiday", { id: loader });
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to save holiday changes", { id: loader });
    }
  };

  // Helper filter function for a raw holiday configuration object
  const isRawHolidayMatching = (raw: any): boolean => {
    if (!raw) return true;

    // 1. Filter by Department
    if (filterDepartmentId) {
      if (raw.applyTo === "DEPARTMENT") {
        if (!raw.departmentIds?.includes(filterDepartmentId)) return false;
      } else if (raw.applyTo === "EMPLOYEE") {
        // Check if any employee listed in raw.employeeIds belongs to selected department
        const matchingEmps = employees.filter(emp => (emp.id || emp._id) && raw.employeeIds?.includes(emp.id || emp._id));
        const inDept = matchingEmps.some(emp => emp.departmentId === filterDepartmentId || emp.department === filterDepartmentId);
        if (!inDept) return false;
      } else if (raw.applyTo === "COMPANY") {
        // Company holidays apply to everyone, so keep it
      }
    }

    // 2. Filter by Employee
    if (filterEmployeeId) {
      if (raw.applyTo === "EMPLOYEE") {
        if (!raw.employeeIds?.includes(filterEmployeeId)) return false;
      } else if (raw.applyTo === "DEPARTMENT") {
        // Check if the employee belongs to any of the holiday's departments
        const empObj = employees.find(emp => (emp.id || emp._id) === filterEmployeeId);
        const inDept = empObj && (raw.departmentIds?.includes(empObj.departmentId) || raw.departmentIds?.includes(empObj.department));
        if (!inDept) return false;
      } else if (raw.applyTo === "COMPANY") {
        // Company holidays apply to everyone, so keep it
      }
    }

    return true;
  };

  // Combine calendar holidays, upcoming holidays, and default national holidays
  const combinedHolidays = [...(Array.isArray(holidays) ? holidays : [])];
  
  // 1. Merge upcoming holidays from API
  if (Array.isArray(upcomingHolidays)) {
    upcomingHolidays.forEach((uh) => {
      const exists = combinedHolidays.some(
        (h) => h.id === uh.id || (h.name === uh.name && h.date === uh.date)
      );
      if (!exists) {
        combinedHolidays.push(uh);
      }
    });
  }

  // 2. Merge default national holidays
  DEFAULT_NATIONAL_HOLIDAYS.forEach((defH) => {
    const exists = combinedHolidays.some(
      (h) => h.name.toLowerCase() === defH.name.toLowerCase() && h.date.toLowerCase() === defH.date.toLowerCase()
    );
    if (!exists) {
      combinedHolidays.push(defH);
    }
  });

  // 3. Generate Week-offs for all Sundays of the currently selected month
  const daysInMonth = new Date(holidayMonth.year, holidayMonth.month + 1, 0).getDate();
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(holidayMonth.year, holidayMonth.month, i);
    if (d.getDay() === 0) { // Sunday
      const dateStr = formatDateStr(d);
      const exists = combinedHolidays.some(h => h.date.toLowerCase() === dateStr.toLowerCase());
      if (!exists) {
        combinedHolidays.push({
          id: `weekoff-${dateStr}`,
          name: "Week-off",
          date: dateStr,
          color: "#A855F7", // Light Purple color code
          raw: {
            title: "Week-off",
            description: "Weekly Off",
            holidayDate: d.toISOString(),
            holidayType: "COMPANY",
            mode: "SINGLE",
            applyTo: "COMPANY",
            isWeekOff: true
          }
        });
      }
    }
  }

  // Filter mapped calendar day holiday tags client side
  const filteredHolidays = combinedHolidays.filter(h => isRawHolidayMatching(h.raw));

  // Combine raw calendar holidays, raw upcoming holidays, and default national raw holidays
  const combinedRawHolidays = [...(Array.isArray(rawHolidays) ? rawHolidays : [])];
  
  if (Array.isArray(upcomingHolidays)) {
    upcomingHolidays.forEach((uh) => {
      if (uh.raw) {
        const exists = combinedRawHolidays.some(
          (rh) => (rh.id || rh._id) === (uh.raw.id || uh.raw._id)
        );
        if (!exists) {
          combinedRawHolidays.push(uh.raw);
        }
      }
    });
  }

  DEFAULT_NATIONAL_HOLIDAYS.forEach((defH) => {
    if (defH.raw) {
      const exists = combinedRawHolidays.some(
        (rh) => rh.title.toLowerCase() === defH.raw.title.toLowerCase() && rh.holidayDate === defH.raw.holidayDate
      );
      if (!exists) {
        combinedRawHolidays.push(defH.raw);
      }
    }
  });

  // Filter raw holiday configurations client side for Table list
  const filteredRawHolidays = combinedRawHolidays.filter(rawH => isRawHolidayMatching(rawH));

  // Sort upcoming holidays
  const apiUpcomingNational = [...upcomingHolidays].filter(
    (h) => h.raw?.holidayType === "NATIONAL" || !h.raw
  );
  
  const mergedNationalHolidays = [...apiUpcomingNational];
  DEFAULT_NATIONAL_HOLIDAYS.forEach((defH) => {
    const exists = apiUpcomingNational.some(
      (h) => h.name.toLowerCase() === defH.name.toLowerCase()
    );
    if (!exists) {
      mergedNationalHolidays.push(defH);
    }
  });

  const sortedHolidays = mergedNationalHolidays.sort(
    (a, b) => parseHolidayDate(a.date).getTime() - parseHolidayDate(b.date).getTime()
  );

  return (
    <div className="w-full mx-auto mt-2 space-y-4 animate-in fade-in duration-300 font-sans">
      
      {/* ─── Content Area ────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4 w-full items-start">
        
        {/* Sidebar: Upcoming Holidays */}
        <div className="w-full lg:w-64 flex-shrink-0 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-white/20 shadow-sm p-4 flex flex-col rounded-2xl relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FBD037]"></div>
            </div>
          )}
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-dashed border-gray-150">
            <h3 className="font-semibold text-[#212121] text-sm flex items-center gap-1.5">
              <IconCalendar size={16} className="text-gray-400" />
              Upcoming Holidays
            </h3>
            <span className="w-5 h-5 rounded-full bg-[#FBD037]/20 text-[#C9A100] text-[10px] font-bold flex items-center justify-center border border-[#FBD037]/30">
              {sortedHolidays.length}
            </span>
          </div>

          <div className="flex flex-col gap-3 flex-1 overflow-y-auto pr-1 hide-scrollbar max-h-[70vh]">
            {sortedHolidays.length === 0 ? (
              <span className="text-xs text-gray-400 text-center py-4">No holidays scheduled</span>
            ) : (
              sortedHolidays.map((h, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 p-1.5 rounded-xl transition"
                >
                  <div
                    className="w-1.5 h-8 rounded-full flex-shrink-0"
                    style={{ background: h.color }}
                  />
                  <div>
                    <div className="text-xs font-bold text-[#212121]">{h.name}</div>
                    <div className="text-[10px] text-gray-400 font-semibold">{h.date}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main: Calendar Grid and Header Joined */}
        <div className="flex-1 flex flex-col w-full bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-hidden">
          
          {/* Joined Header */}
          <div className="px-6 py-2 flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-50">
            <div className="flex items-center gap-6">
              <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium whitespace-nowrap">
                Holidays
              </h1>

              {/* Calendar navigation controls */}
              <div className="flex items-center gap-2 bg-white px-2.5 py-1 rounded-lg border border-gray-200 shadow-sm">
                <button
                  onClick={() =>
                    setHolidayMonth((m) => {
                      const d = new Date(m.year, m.month - 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })
                  }
                  className="p-1 hover:bg-[#FBD037]/10 hover:text-[#C9A100] rounded transition cursor-pointer text-gray-400"
                >
                  <IconChevronLeft size={16} />
                </button>
                <span className="text-[#212121] text-xs font-bold whitespace-nowrap min-w-[90px] text-center">
                  {new Date(holidayMonth.year, holidayMonth.month).toLocaleString("default", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <button
                  onClick={() =>
                    setHolidayMonth((m) => {
                      const d = new Date(m.year, m.month + 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })
                  }
                  className="p-1 hover:bg-[#FBD037]/10 hover:text-[#C9A100] rounded transition cursor-pointer text-gray-400"
                >
                  <IconChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Filter button inside toolbar */}
              <button
                onClick={() => {
                  setRightPanelType("filter");
                  setIsRightPanelOpen(true);
                }}
                className={`flex items-center gap-1.5 px-3 h-8 rounded-lg transition shadow-sm hover:shadow-md cursor-pointer text-xs font-semibold ${
                  isRightPanelOpen && rightPanelType === "filter"
                    ? "bg-[#FBD037] text-[#212121]"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
                title="Filter Holidays"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.59l-5.432 5.432a2.25 2.25 0 00-.659 1.59v3.49a2.25 2.25 0 01-.25 1.025l-1.51 2.94a.75.75 0 01-1.307-.327V14.75a2.25 2.25 0 00-.659-1.59l-5.432-5.432a2.25 2.25 0 01-.659-1.59V4.774c0-.54.384-1.006.917-1.096A48.116 48.116 0 0112 3z" />
                </svg>
                Filter {(filterDepartmentId || filterEmployeeId) && "•"}
              </button>

              {/* Import button inside toolbar */}
              <button
                onClick={() => {
                  setRightPanelType("import");
                  setIsRightPanelOpen(true);
                }}
                className={`flex items-center justify-center w-8 h-8 rounded-lg transition shadow-sm hover:shadow-md active:scale-95 cursor-pointer ${
                  isRightPanelOpen && rightPanelType === "import"
                    ? "bg-[#FBD037] text-[#212121]"
                    : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
                }`}
                title="Import Holidays"
              >
                <IconUpload stroke={2.5} size={16} />
              </button>

              {/* Add button inside toolbar */}
              <button
                onClick={handleAddSingleClick}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                title="Add Holiday"
              >
                <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
              </button>

              {/* Layout view toggle buttons */}
              <div className="flex items-center gap-1 bg-white p-0.5 h-8 rounded-lg border border-slate-200 ml-1.5">
                <button
                  type="button"
                  onClick={() => setViewMode("calendar")}
                  className={`flex items-center justify-center w-7 h-7 rounded-md transition cursor-pointer ${
                    viewMode === "calendar"
                      ? "bg-[#FBD037] text-[#212121] shadow-sm"
                      : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                  }`}
                  title="Calendar Grid"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={`flex items-center justify-center w-7 h-7 rounded-md transition cursor-pointer ${
                    viewMode === "table"
                      ? "bg-[#FBD037] text-[#212121] shadow-sm"
                      : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                  }`}
                  title="Holidays Table View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Table body joined with header */}
          <div className="p-4 flex-1 flex flex-col bg-transparent relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-b-3xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FBD037]"></div>
              </div>
            )}

            {viewMode === "calendar" ? (
              <div className="w-full flex-1 flex flex-col justify-between">
                
                {/* Days of week header */}
                <div className="grid grid-cols-7 gap-px mb-2 bg-slate-100/50 py-1.5 rounded-xl border border-slate-200/30 flex-shrink-0">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div
                       key={day}
                       className="text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1.5 flex-1 items-stretch">
                  {/* Empty padding days for first week */}
                  {Array.from({
                    length: new Date(holidayMonth.year, holidayMonth.month, 1).getDay(),
                  }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="rounded-2xl bg-gray-50/20 border border-transparent min-h-[64px]"
                    />
                  ))}

                  {/* Actual days */}
                  {Array.from(
                    { length: new Date(holidayMonth.year, holidayMonth.month + 1, 0).getDate() },
                    (_, i) => {
                      const dayNum = i + 1;
                      const d = new Date(holidayMonth.year, holidayMonth.month, dayNum);
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;

                      const today = new Date();
                      const isToday =
                        today.getDate() === dayNum &&
                        today.getMonth() === holidayMonth.month &&
                        today.getFullYear() === holidayMonth.year;

                      const padDay = String(dayNum).padStart(2, "0");
                      const monthStr = new Date(holidayMonth.year, holidayMonth.month).toLocaleString(
                        "default",
                        { month: "short" }
                      );
                      const dayMatchStr = `${padDay} ${monthStr} ${holidayMonth.year}`;
                      const holidayObj = filteredHolidays.find(
                        (h) => h.date.toLowerCase() === dayMatchStr.toLowerCase()
                      );

                      return (
                        <div
                          key={dayNum}
                          onClick={() => handleDayClick(dayNum)}
                          className={`p-2 rounded-2xl border cursor-pointer flex flex-col justify-between transition-all select-none min-h-[64px] ${
                            isToday
                              ? "border-[#FBD037] bg-[#FBD037]/5 shadow-[0_0_8px_rgba(251,208,55,0.25)] hover:scale-102"
                              : isWeekend
                              ? "bg-purple-50/20 border-purple-100/30 hover:border-purple-200 hover:bg-purple-50/30"
                              : "bg-white/60 border-slate-100/50 hover:border-[#FBD037]/50 shadow-sm hover:shadow hover:-translate-y-0.5"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div
                              className={`text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                                isToday
                                  ? "bg-[#FBD037] text-[#212121]"
                                  : isWeekend
                                  ? "text-purple-600 bg-purple-100/50"
                                  : "text-[#212121]"
                              }`}
                            >
                              {dayNum}
                            </div>
                            {isToday && (
                              <span className="text-[7px] font-black uppercase tracking-wider text-[#C9A100] bg-[#FBD037]/20 px-1.5 py-0.5 rounded-full border border-[#FBD037]/40 shadow-sm">
                                Today
                              </span>
                            )}
                          </div>

                          <div className="flex flex-col gap-1 mt-1">
                            {holidayObj && (
                              <span
                                className="text-[8px] px-1.5 py-0.5 rounded text-white font-bold truncate w-full shadow-sm text-center animate-in fade-in duration-300"
                                style={{ background: holidayObj.color }}
                              >
                                {holidayObj.name}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>

              </div>
            ) : (
              /* Holidays Day-by-Day List View */
              <div className="flex-1 flex flex-col bg-white rounded-b-2xl">
                {/* Feed Header */}
                <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-[#FCFDF8]">
                  <h2 className="text-[#212121] text-sm font-bold uppercase tracking-wide">
                    {new Date(holidayMonth.year, holidayMonth.month).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h2>
                </div>

                {/* Feed Rows */}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100 pr-1 max-h-[calc(100vh-280px)]">
                  {Array.from(
                    { length: new Date(holidayMonth.year, holidayMonth.month + 1, 0).getDate() },
                    (_, i) => {
                      const dayNum = i + 1;
                      const d = new Date(holidayMonth.year, holidayMonth.month, dayNum);
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      
                      const padDay = String(dayNum).padStart(2, "0");
                      const monthStr = d.toLocaleString("default", { month: "short" }).toUpperCase();
                      const weekdayStr = d.toLocaleString("default", { weekday: "short" }).toUpperCase();
                      const rowDateStr = `${padDay} ${monthStr}, ${weekdayStr}`;

                      const dayMatchStr = `${padDay} ${d.toLocaleString("default", { month: "short" })} ${holidayMonth.year}`;
                      const dayHolidays = filteredHolidays.filter(
                        (h) => h.date.toLowerCase() === dayMatchStr.toLowerCase()
                      );

                      return (
                        <div key={dayNum} className="flex items-center py-4 px-6 hover:bg-slate-50/20 transition-colors">
                          {/* Date label column */}
                          <div className="w-36 text-xs font-bold text-[#212121] tracking-wide shrink-0">
                            {rowDateStr}
                          </div>

                          {/* Badges/Status column */}
                          <div className="flex items-center gap-3 flex-wrap flex-1">
                            {dayHolidays.map((h, hIdx) => (
                              <div
                                key={hIdx}
                                onClick={() => {
                                  if (h.raw) {
                                    handleHolidayClick(h.raw);
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100/50 text-[10px] font-bold text-emerald-600 shadow-sm cursor-pointer hover:bg-emerald-100/60 transition duration-150 animate-in fade-in duration-200"
                              >
                                <span>{h.name}</span>
                                <IconClock size={12} className="text-emerald-400" />
                              </div>
                            ))}

                            {dayHolidays.length === 0 && (
                              <span className="text-xs text-gray-400 italic">No holidays</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar Panel: Add Holiday, Import Form, Filters, View Details, or Edit Details */}
        {isRightPanelOpen && (
          <div className="w-full lg:w-[430px] flex-shrink-0 border border-gray-200 rounded-2xl bg-[#FCFDF8] shadow-sm flex flex-col h-[calc(100vh-170px)] sticky top-[95px] overflow-hidden animate-in slide-in-from-right-10 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 bg-white/50 shrink-0">
              <div>
                <h2 className="text-xl font-semibold text-[#212121]">
                  {rightPanelType === "add"
                    ? "Create / Edit Holiday"
                    : rightPanelType === "import"
                    ? "Import Holidays"
                    : rightPanelType === "view"
                    ? "Holiday Details"
                    : rightPanelType === "edit"
                    ? "Edit Holiday"
                    : "Filter Holidays"}
                </h2>
                {rightPanelType === "add" && (
                  <p className="text-sm text-[#212121]/70 mt-0.5">
                    Configure and save holiday details below
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {rightPanelType === "view" && viewingHoliday && (
                  <div className="relative">
                    {/* Three-dots menu button */}
                    <button
                      type="button"
                      onClick={() => setIsDotsMenuOpen(!isDotsMenuOpen)}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                      title="Actions Menu"
                    >
                      <IconDotsVertical size={16} />
                    </button>

                    {/* Dropdown Options List */}
                    {isDotsMenuOpen && (
                      <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-150 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                        <button
                          type="button"
                          onClick={() => {
                            setIsDotsMenuOpen(false);
                            
                            const hDate = viewingHoliday.holidayDate 
                              ? viewingHoliday.holidayDate.split("T")[0] 
                              : (viewingHoliday.startDate ? viewingHoliday.startDate.split("T")[0] : "");
                            
                            setEditingHolidayForm({
                              id: viewingHoliday.isWeekOff ? null : (viewingHoliday.id || viewingHoliday._id || null),
                              date: hDate,
                              title: viewingHoliday.title,
                              description: viewingHoliday.description,
                              isWeekOff: !!viewingHoliday.isWeekOff,
                            });
                            
                            setRightPanelType("edit");
                          }}
                          className="w-full text-left px-3.5 py-2 text-xs font-bold text-gray-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                          </svg>
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            setIsDotsMenuOpen(false);
                            if (viewingHoliday.isWeekOff) {
                              toast.error("Standard weekly off days cannot be deleted.", { duration: 3000 });
                              return;
                            }
                            setIsRightPanelOpen(false);
                            const loader = toast.loading("Deleting holiday...");
                            try {
                              const res = await deleteHrHoliday(viewingHoliday.id || viewingHoliday._id || viewingHoliday);
                              if (res.success) {
                                toast.success("Holiday deleted successfully", { id: loader });
                                loadCalendarHolidays();
                              } else {
                                toast.error(res.message || "Failed to delete holiday", { id: loader });
                              }
                            } catch (err: any) {
                              toast.error(err.message || "Failed to delete holiday", { id: loader });
                            }
                          }}
                          className="w-full text-left px-3.5 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 cursor-pointer border-t border-gray-100"
                        >
                          <IconTrash size={14} className="text-red-400" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setIsRightPanelOpen(false)}
                  className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                >
                  <IconX size={16} />
                </button>
              </div>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto min-h-0 bg-white">
              {rightPanelType === "add" ? (
                <AddHolidayForm
                  onClose={() => setIsRightPanelOpen(false)}
                  initialDate={selectedDate}
                  initialMode={modalMode}
                  initialHoliday={selectedHoliday}
                  employeesList={employees}
                  departmentsList={departments}
                  onAddHoliday={handleAddHolidaySubmit}
                />
              ) : rightPanelType === "import" ? (
                <ImportHolidayForm
                  onClose={() => setIsRightPanelOpen(false)}
                  onImportSuccess={() => {
                    loadCalendarHolidays();
                    setIsRightPanelOpen(false);
                  }}
                />
              ) : rightPanelType === "filter" ? (
                <FilterHolidayForm
                  onClose={() => setIsRightPanelOpen(false)}
                  employeesList={employees}
                  departmentsList={departments}
                  filterDepartmentId={filterDepartmentId}
                  setFilterDepartmentId={setFilterDepartmentId}
                  filterEmployeeId={filterEmployeeId}
                  setFilterEmployeeId={setFilterEmployeeId}
                  onClearFilters={() => {
                    setFilterDepartmentId("");
                    setFilterEmployeeId("");
                    setIsRightPanelOpen(false);
                  }}
                />
              ) : rightPanelType === "view" && viewingHoliday ? (
                <div className="flex flex-col h-full justify-between">
                  <div className="p-6 space-y-5 flex-1 overflow-y-auto text-slate-700">
                    {/* Category tag */}
                    {viewingHoliday.isWeekOff ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-purple-50 text-purple-600 border border-purple-100/50 shadow-sm animate-in fade-in duration-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></span>
                        Special Holiday
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100/50 shadow-sm animate-in fade-in duration-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Public Holiday
                      </span>
                    )}

                    {/* Title and Description */}
                    <div className="space-y-1">
                      <h4 className="text-xl font-extrabold text-slate-800 tracking-tight leading-snug">
                        {viewingHoliday.title}
                      </h4>
                      <p className="text-xs text-gray-500 font-medium leading-relaxed">
                        {viewingHoliday.description || "No description provided."}
                      </p>
                    </div>

                    {/* Date Details Box */}
                    <div className="p-4 rounded-xl border border-gray-100 bg-[#FCFDF8] flex items-center gap-4.5 shadow-sm">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 text-purple-500 flex items-center justify-center shrink-0">
                        <IconCalendar size={20} />
                      </div>
                      <div className="space-y-0.5">
                        <div className="text-xs font-bold text-slate-800">
                          {viewingHoliday.mode === "SINGLE" 
                            ? formatFullDateString(viewingHoliday.holidayDate)
                            : `${formatFullDateString(viewingHoliday.startDate)} to ${formatFullDateString(viewingHoliday.endDate)}`}
                        </div>
                        <div className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider">
                          {viewingHoliday.mode === "SINGLE"
                            ? formatShortDateString(viewingHoliday.holidayDate)
                            : `${formatShortDateString(viewingHoliday.startDate)} - ${formatShortDateString(viewingHoliday.endDate)}`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-100 bg-slate-50/50 flex items-center justify-end shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsRightPanelOpen(false)}
                      className="px-5 py-2 border border-gray-200 hover:bg-gray-100 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : rightPanelType === "edit" && editingHolidayForm ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const loader = toast.loading("Saving changes...");
                    try {
                      let res;
                      if (editingHolidayForm.id) {
                        const editPayload: HrHolidayPayload = {
                          title: editingHolidayForm.title,
                          description: editingHolidayForm.description,
                          holidayDate: editingHolidayForm.date,
                          holidayType: editingHolidayForm.isWeekOff ? "COMPANY" : "NATIONAL",
                        };
                        res = await updateHrHoliday(editingHolidayForm.id, editPayload);
                      } else {
                        const createPayload: HrHolidayPayload = {
                          actionType: "CREATE",
                          mode: "SINGLE",
                          applyTo: "COMPANY",
                          title: editingHolidayForm.title,
                          description: editingHolidayForm.description,
                          holidayType: editingHolidayForm.isWeekOff ? "COMPANY" : "NATIONAL",
                          holidayDate: editingHolidayForm.date,
                          branchIds: [],
                          departmentIds: [],
                          employeeIds: [],
                          selectedDays: [],
                        };
                        res = await createHrHoliday(createPayload);
                      }

                      if (res.success) {
                        toast.success(editingHolidayForm.id ? "Holiday updated successfully" : "Holiday created successfully", { id: loader });
                        setIsRightPanelOpen(false);
                        loadCalendarHolidays();
                      } else {
                        toast.error(res.message || "Failed to update holiday", { id: loader });
                      }
                    } catch (err: any) {
                      toast.error(err.message || "Failed to save holiday changes", { id: loader });
                    }
                  }}
                  className="p-6 space-y-4"
                >
                  {/* Date Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={editingHolidayForm.date}
                      onChange={(e) => setEditingHolidayForm({ ...editingHolidayForm, date: e.target.value })}
                      className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                    />
                  </div>

                  {/* Title Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter holiday title"
                      value={editingHolidayForm.title}
                      onChange={(e) => setEditingHolidayForm({ ...editingHolidayForm, title: e.target.value })}
                      className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition"
                    />
                  </div>

                  {/* Description Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={3}
                      placeholder="Enter holiday description"
                      value={editingHolidayForm.description}
                      onChange={(e) => setEditingHolidayForm({ ...editingHolidayForm, description: e.target.value })}
                      className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] transition resize-none"
                    />
                  </div>

                  {/* Footer Buttons */}
                  <div className="pt-2 flex items-center justify-end gap-2.5">
                    <button
                      type="button"
                      onClick={() => setRightPanelType("view")}
                      className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition cursor-pointer bg-black hover:bg-slate-800 text-white flex items-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
